import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  formatDateToWeekdayString,
  BaseButton,
  Chip,
  Modal,
  Toast,
} from '@repo/common';

import { ReactComponent as NavigationLeftIcon } from '@repo/common/assets/icons/24px/icon_24px_navigation-left.svg';
import { ReactComponent as EditIcon } from '@repo/common/assets/icons/16px/icon_16px_edit_outline.svg';
import { ReactComponent as CloseIcon } from '@repo/common/assets/icons/16px/icon_16px_clock-alarm_outline.svg';
import { ReactComponent as InvisibleIcon } from '@repo/common/assets/icons/16px/icon_16px_invisible-close_outline.svg';
import { ReactComponent as VisibleIcon } from '@repo/common/assets/icons/16px/icon_16px_visible_outline.svg';
import { ReactComponent as DeleteIcon } from '@repo/common/assets/icons/20px/icon_20px_delete_outline.svg';

import {
  getWorkshopDetail,
  softDeleteWorkshopSlots,
  updateSlotHidden,
  updateSlotStatus,
} from '../../../api/workshop/workshop.api';

const formatTime = (time) => {
  const [hour, minute] = time.split(':').map(Number);
  const period = hour < 12 ? 'AM' : 'PM';
  return `${hour}:${String(minute).padStart(2, '0')}${period}`;
};

const WorkshopDetailHeader = ({
  onSlotSelect,
  selectedSlotId,
  onDeleteSuccess,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const slotIdFromUrl = searchParams.get('slotId');

  const { workshopId } = useParams();
  const [workshop, setWorkshop] = useState(null);
  const [showDeleteWorkshopModal, setShowDeleteWorkshopModal] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showHideSuccessToast, setShowHideSuccessToast] = useState(false);
  const [showCloseSuccessToast, setShowCloseSuccessToast] = useState(false);

  const selectedSlot = workshop?.dateSlots?.find(
    (slot) => slot.id === selectedSlotId,
  );
  const isHidden = selectedSlot?.isHidden;
  const isClosed = selectedSlot?.status === 'CLOSED';

  const handleSlotSelect = (slotId) => {
    setSearchParams({ slotId }, { replace: true });
    onSlotSelect(slotId);
  };

  useEffect(() => {
    const fetchWorkshop = async () => {
      try {
        const data = await getWorkshopDetail(workshopId);
        setWorkshop(data);

        if (data?.dateSlots?.length > 0) {
          let initialSlot;

          if (slotIdFromUrl) {
            initialSlot = data.dateSlots.find(
              (s) => s.id === Number(slotIdFromUrl),
            );
          }

          if (!initialSlot) {
            initialSlot = data.dateSlots[0];
            setSearchParams({ slotId: initialSlot.id });
          }

          onSlotSelect(initialSlot.id);
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchWorkshop();
  }, [workshopId, slotIdFromUrl, onSlotSelect, setSearchParams]);

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await softDeleteWorkshopSlots([slotIdFromUrl]);
      setShowDeleteWorkshopModal(false);
      onDeleteSuccess?.();
    } catch (error) {
      console.error('Delete failed:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleHide = async () => {
    if (!selectedSlotId) return;

    try {
      await updateSlotHidden(selectedSlotId, !isHidden);

      setWorkshop((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          dateSlots: prev.dateSlots.map((slot) =>
            slot.id === selectedSlotId
              ? { ...slot, isHidden: !isHidden }
              : slot,
          ),
        };
      });

      setShowHideSuccessToast(false);
      setTimeout(() => setShowHideSuccessToast(true), 10);
    } catch (error) {
      console.error('Toggle hide failed:', error);
    }
  };

  const handleToggleClose = async () => {
    if (!selectedSlotId) return;

    try {
      const nextStatus = isClosed ? 'OPEN' : 'CLOSED';

      await updateSlotStatus(selectedSlotId, nextStatus);

      setWorkshop((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          dateSlots: prev.dateSlots.map((slot) =>
            slot.id === selectedSlotId ? { ...slot, status: nextStatus } : slot,
          ),
        };
      });

      setShowCloseSuccessToast(false);
      setTimeout(() => setShowCloseSuccessToast(true), 10);
    } catch (error) {
      console.error('Toggle close failed:', error);
    }
  };

  return (
    <div className='flex flex-col items-start gap-6 pb-10 w-full'>
      <div className='flex items-center gap-2 w-full'>
        <div className='flex itmes-center gap-2 flex-1'>
          <button
            className='text-icon-primary hover:text-icon-secondary'
            onClick={() => navigate('/mypage?tab=Workshop%20Management')}
          >
            <NavigationLeftIcon />
          </button>
          <div className='typo-title1 text-text-primary'>{workshop?.title}</div>
        </div>

        <div className='flex items-end gap-2'>
          <BaseButton
            type='text_basic'
            size='s'
            icon={<EditIcon />}
            gap='gap-1'
            bgclass='py-1.5 pl-2 pr-3'
            width='w-[65px]'
            onClick={() => navigate(`/admin/workshop/${workshopId}/edit`)}
          >
            Edit
          </BaseButton>
          <BaseButton
            type='text_basic'
            size='s'
            icon={<CloseIcon />}
            gap='gap-1'
            bgclass='py-1.5 pl-2 pr-3'
            width='w-[75px]'
            onClick={handleToggleClose}
          >
            {isClosed ? 'Reopen' : 'Close'}
          </BaseButton>
          <BaseButton
            type='text_basic'
            size='s'
            icon={isHidden ? <VisibleIcon /> : <InvisibleIcon />}
            gap='gap-1'
            bgclass='py-1.5 pl-2 pr-3'
            width='w-[69px]'
            onClick={handleToggleHide}
          >
            {isHidden ? 'Unhide' : 'Hide'}
          </BaseButton>
          <button
            className='flex w-[30px] h-[30px] justify-center items-center gap-[10px] rounded-lg text-icon-secondary'
            onClick={() => setShowDeleteWorkshopModal(true)}
          >
            <DeleteIcon />
          </button>
        </div>
      </div>

      <div
        className='flex items-center gap-2 overflow-x-auto w-full pb-2
        [&::-webkit-scrollbar]:h-1.5
        [&::-webkit-scrollbar-track]:bg-transparent
        [&::-webkit-scrollbar-thumb]:bg-grayscale-gray04
        [&::-webkit-scrollbar-thumb]:rounded-[10px]'
      >
        {workshop?.dateSlots?.map((slot) => (
          <Chip
            key={slot.id}
            type='fill'
            state={selectedSlotId === slot.id ? 'selected' : 'default'}
            hideIcon
            onClick={() => handleSlotSelect(slot.id)}
          >
            <span className='whitespace-nowrap cursor-pointer'>
              {formatDateToWeekdayString(slot.date)}∙
              {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
            </span>
          </Chip>
        ))}
      </div>

      {showDeleteWorkshopModal && (
        <Modal
          isOpen
          onClickLeftBtn={() => setShowDeleteWorkshopModal(false)}
          onClickRightBtn={handleDeleteConfirm}
          title='Delete workshop?'
          subText='This will cancel any participant bookings and delete the workshop.
This action cannot be undone..'
          buttonSize='xl'
          leftButtonLabel='Keep Workshop'
          leftHideIcon={true}
          rightButtonLabel='Delete & Cancel All'
          rightHideIcon={true}
          type='criticalError'
          rightButtonLoading={isDeleting}
        />
      )}

      {showHideSuccessToast && (
        <div className='fixed bottom-6 left-0 right-0 px-4 z-[1000] flex justify-center'>
          <div
            className={`w-full ${
              isHidden
                ? 'max-w-[223px] tabletH:w-[223px]'
                : 'max-w-[234px] tabletH:w-[234px]'
            }`}
          >
            <Toast
              title={isHidden ? 'Workshop hidden.' : 'Workshop restored.'}
              hierarchy='important'
              direction='up'
              customWidth='100%'
            />
          </div>
        </div>
      )}

      {showCloseSuccessToast && (
        <div className='fixed bottom-6 left-0 right-0 px-4 z-[1000] flex justify-center'>
          <div
            className={`w-full ${
              isClosed
                ? 'max-w-[221px] tabletH:w-[221px]'
                : 'max-w-[242px] tabletH:w-[242px]'
            }`}
          >
            <Toast
              title={isClosed ? 'Workshop closed.' : 'Workshop reopened.'}
              hierarchy='important'
              direction='up'
              customWidth='100%'
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkshopDetailHeader;
